import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import Reveal from "../components/Reveal";
import { galleryImages } from "../mock";

// asymmetric editorial mosaic pattern (cycles every 6 tiles)
const mdSpans = [
  "md:col-span-8 md:row-span-2",
  "md:col-span-4 md:row-span-1",
  "md:col-span-4 md:row-span-1",
  "md:col-span-4 md:row-span-2",
  "md:col-span-8 md:row-span-1",
  "md:col-span-8 md:row-span-1",
];

const PageHero = () => (
  <section className="relative h-[62vh] min-h-[420px] flex items-end overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${galleryImages[5]})` }} />
    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/40" />
    <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pb-14">
      <div className="flex items-center gap-4 mb-5">
        <span className="w-10 h-px bg-[#d9b877]" />
        <p className="eyebrow text-[#e7cf9c]">Safranbolu Çelebi Otel</p>
      </div>
      <h1 className="font-display text-6xl md:text-8xl text-white font-500 leading-none animate-fade-up">
        Galeri
      </h1>
      <p className="text-white/75 mt-4 max-w-md text-[15px]">
        Otelimizin ve odalarımızın seçkin karelerinden bir seçki.
      </p>
    </div>
  </section>
);

const Gallery = () => {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = () => setIndex(null);
  const prev = useCallback((e) => {
    if (e) e.stopPropagation();
    setIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length);
  }, []);
  const next = useCallback((e) => {
    if (e) e.stopPropagation();
    setIndex((i) => (i + 1) % galleryImages.length);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next]);

  return (
    <div className="font-body bg-[#fdfbf7]">
      <PageHero />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-12 auto-rows-[44vw] md:auto-rows-[165px] gap-3 md:gap-4 [grid-auto-flow:dense]">
            {galleryImages.map((img, i) => {
              const feature = i % 6 === 0;
              const base = feature ? "col-span-2 row-span-2" : "col-span-1 row-span-1";
              return (
                <Reveal
                  key={i}
                  delay={(i % 4) * 60}
                  className={`${base} ${mdSpans[i % mdSpans.length]}`}
                >
                  <button
                    onClick={() => setIndex(i)}
                    className="mtile w-full h-full block"
                    aria-label={`Görsel ${i + 1}`}
                  >
                    <img src={img} alt={`Çelebi Otel ${i + 1}`} loading="lazy" />
                    <span className="mtile-overlay" />
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-500">
                      <span className="w-11 h-11 rounded-full border border-white/70 text-white flex items-center justify-center backdrop-blur-sm">
                        <Plus size={18} />
                      </span>
                    </span>
                    <span className="mtile-cap eyebrow text-[10px]">
                      {String(i + 1).padStart(2, "0")} · Çelebi Otel
                    </span>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {open && (
        <div onClick={close} className="fixed inset-0 z-[100] bg-[#161310]/95 flex items-center justify-center p-4">
          <button onClick={close} className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors">
            <X size={30} />
          </button>
          <button onClick={prev} className="absolute left-4 md:left-10 text-white/60 hover:text-[#d9b877] transition-colors">
            <ChevronLeft size={46} />
          </button>
          <img
            src={galleryImages[index]}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] object-contain shadow-2xl"
          />
          <button onClick={next} className="absolute right-4 md:right-10 text-white/60 hover:text-[#d9b877] transition-colors">
            <ChevronRight size={46} />
          </button>
          <span className="slide-index absolute bottom-7 left-1/2 -translate-x-1/2 text-white/70 text-xl tracking-widest">
            {String(index + 1).padStart(2, "0")}
            <span className="text-white/30"> / {String(galleryImages.length).padStart(2, "0")}</span>
          </span>
        </div>
      )}
    </div>
  );
};

export default Gallery;
